<template>
  <div class="admin-dashboard">
    <!-- Login Screen -->
    <div v-if="!isAuthenticated" class="login-container">
      <div class="login-card">
        <h1>Admin Login</h1>
        <p>Enter the admin password to access the dashboard</p>
        
        <form @submit.prevent="login">
          <div class="form-group">
            <input
              v-model="password"
              type="password"
              placeholder="Enter admin password"
              class="form-control"
              :disabled="loading"
              autofocus
            />
          </div>
          
          <div v-if="error" class="error-message">
            {{ error }}
          </div>
          
          <button type="submit" class="btn btn-primary" :disabled="loading || !password">
            {{ loading ? 'Logging in...' : 'Login' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Dashboard -->
    <div v-else class="dashboard-container">
      <div class="dashboard-header">
        <h1>Community Keys Dashboard</h1>
        <button @click="logout" class="btn btn-secondary">Logout</button>
      </div>

      <!-- Statistics -->
      <div class="stats-grid" v-if="stats">
        <div class="stat-card">
          <h3>Total Keys</h3>
          <p class="stat-value">{{ stats.total_keys }}</p>
        </div>
        <div class="stat-card">
          <h3>Active Keys</h3>
          <p class="stat-value">{{ stats.active_keys }}</p>
        </div>
        <div class="stat-card">
          <h3>Community Keys</h3>
          <p class="stat-value">{{ stats.by_source?.community || 0 }}</p>
        </div>
        <div class="stat-card">
          <h3>Last 24h Activity</h3>
          <p class="stat-value">{{ stats.recent_activity?.last_24h || 0 }}</p>
        </div>
      </div>

      <!-- Create New Key -->
      <div class="create-key-section">
        <h2>Create New Key</h2>
        <form @submit.prevent="createKey" class="create-key-form">
          <div class="form-row">
            <input
              v-model="newKey.email"
              type="email"
              placeholder="Email address"
              class="form-control"
              required
            />
            <select v-model="newKey.source" class="form-control">
              <option value="community">Community</option>
              <option value="promotional">Promotional</option>
            </select>
            <input
              v-model="newKey.community_name"
              type="text"
              placeholder="Community name (optional)"
              class="form-control"
            />
            <button type="submit" class="btn btn-primary" :disabled="creatingKey">
              {{ creatingKey ? 'Creating...' : 'Create Key' }}
            </button>
          </div>
        </form>
        <div v-if="createSuccess" class="success-message">
          Key created successfully!
        </div>
        <div v-if="createError" class="error-message">
          {{ createError }}
        </div>
      </div>

      <!-- Search and Filter -->
      <div class="search-section">
        <h2>Search Keys</h2>
        <div class="search-controls">
          <input
            v-model="filters.email"
            type="text"
            placeholder="Search by email"
            class="form-control"
            @input="debouncedSearch"
          />
          <select v-model="filters.source" @change="searchKeys" class="form-control">
            <option value="">All Sources</option>
            <option value="paid">Paid</option>
            <option value="community">Community</option>
            <option value="promotional">Promotional</option>
          </select>
          <select v-model="filters.active" @change="searchKeys" class="form-control">
            <option value="">All Keys</option>
            <option value="true">Active Only</option>
            <option value="false">Revoked Only</option>
          </select>
          <button @click="refreshData" class="btn btn-secondary" :disabled="loading">
            <span v-if="loading">Refreshing...</span>
            <span v-else>Refresh</span>
          </button>
        </div>
      </div>

      <!-- Keys Table -->
      <div class="keys-table-section">
        <table class="keys-table" v-if="keys.length > 0">
          <thead>
            <tr>
              <th>Email</th>
              <th>Source</th>
              <th>Community</th>
              <th>Created</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in keys" :key="key.id">
              <td>{{ getDisplayEmail(key) }}</td>
              <td>
                <span :class="['badge', `badge-${key.source}`]">
                  {{ key.source }}
                </span>
              </td>
              <td>{{ key.community_name || '-' }}</td>
              <td>{{ formatDate(key.created_at) }}</td>
              <td>
                <span :class="['status', key.is_active && !key.revoked_at ? 'active' : 'inactive']">
                  {{ key.is_active && !key.revoked_at ? 'Active' : 'Revoked' }}
                </span>
              </td>
              <td>
                <button
                  v-if="key.is_active && !key.revoked_at"
                  @click="revokeKey(key.id)"
                  class="btn btn-small btn-danger"
                  :disabled="revokingKey === key.id"
                >
                  {{ revokingKey === key.id ? 'Revoking...' : 'Revoke' }}
                </button>
                <button
                  @click="copyKey(key.key)"
                  class="btn btn-small btn-secondary"
                >
                  Copy Key
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div v-else-if="loading" class="loading-state">
          Loading keys...
        </div>
        
        <div v-else class="empty-state">
          No keys found
        </div>

        <!-- Pagination -->
        <div v-if="pagination.hasMore" class="pagination">
          <button
            @click="loadMore"
            class="btn btn-secondary"
            :disabled="loading"
          >
            Load More
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'

export default {
  name: 'AdminDashboard',
  setup() {
    // Auth state
    const isAuthenticated = ref(false)
    const authToken = ref('')
    const password = ref('')
    const loading = ref(false)
    const error = ref('')

    // Dashboard state
    const keys = ref([])
    const stats = ref(null)
    const filters = reactive({
      email: '',
      source: '',
      active: '',
      limit: 50,
      offset: 0
    })
    const pagination = reactive({
      hasMore: false
    })

    // Create key state
    const newKey = reactive({
      email: '',
      source: 'community',
      community_name: ''
    })
    const creatingKey = ref(false)
    const createSuccess = ref(false)
    const createError = ref('')

    // Revoke key state
    const revokingKey = ref(null)

    // Check for existing auth
    onMounted(() => {
      const savedToken = localStorage.getItem('adminToken')
      if (savedToken) {
        authToken.value = savedToken
        isAuthenticated.value = true
        loadDashboard()
      }
    })

    // Login function
    const login = async () => {
      loading.value = true
      error.value = ''

      try {
        const response = await fetch('/.netlify/functions/admin-auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: password.value })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          authToken.value = data.token
          localStorage.setItem('adminToken', data.token)
          isAuthenticated.value = true
          password.value = ''
          loadDashboard()
        } else {
          error.value = data.message || 'Login failed'
        }
      } catch (err) {
        error.value = 'Network error. Please try again.'
        console.error('Login error:', err)
      } finally {
        loading.value = false
      }
    }

    // Logout function
    const logout = () => {
      authToken.value = ''
      localStorage.removeItem('adminToken')
      isAuthenticated.value = false
      keys.value = []
      stats.value = null
    }

    // Load dashboard data
    const loadDashboard = async () => {
      loading.value = true
      try {
        await Promise.all([
          searchKeys(),
          loadStats()
        ])
      } catch (err) {
        console.error('Dashboard load error:', err)
      } finally {
        loading.value = false
      }
    }

    // Search keys
    const searchKeys = async (append = false) => {
      if (!append) {
        filters.offset = 0
        keys.value = []
      }

      loading.value = true
      try {
        const params = new URLSearchParams({
          limit: filters.limit,
          offset: filters.offset
        })

        if (filters.email) params.append('email', filters.email)
        if (filters.source) params.append('source', filters.source)
        if (filters.active) params.append('active', filters.active)

        const response = await fetch(`/.netlify/functions/admin-community-keys?${params}`, {
          headers: {
            'Authorization': `Bearer ${authToken.value}`
          }
        })

        if (response.status === 401) {
          logout()
          return
        }

        const data = await response.json()
        if (data.success) {
          if (append) {
            keys.value.push(...data.data)
          } else {
            keys.value = data.data
          }
          pagination.hasMore = data.pagination?.hasMore || false
        }
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        loading.value = false
      }
    }

    // Load more keys
    const loadMore = () => {
      filters.offset += filters.limit
      searchKeys(true)
    }

    // Load statistics
    const loadStats = async () => {
      try {
        const response = await fetch('/.netlify/functions/admin-community-keys?include_stats=true&limit=1', {
          headers: {
            'Authorization': `Bearer ${authToken.value}`
          }
        })

        const data = await response.json()
        if (data.success && data.stats) {
          stats.value = data.stats
        }
      } catch (err) {
        console.error('Stats error:', err)
      }
    }

    // Create new key
    const createKey = async () => {
      creatingKey.value = true
      createSuccess.value = false
      createError.value = ''

      try {
        const response = await fetch('/.netlify/functions/admin-community-keys', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken.value}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: newKey.email,
            source: newKey.source,
            community_name: newKey.community_name || null
          })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          createSuccess.value = true
          newKey.email = ''
          newKey.community_name = ''
          setTimeout(() => createSuccess.value = false, 3000)
          refreshData()
        } else {
          createError.value = data.message || 'Failed to create key'
        }
      } catch (err) {
        createError.value = 'Network error. Please try again.'
        console.error('Create key error:', err)
      } finally {
        creatingKey.value = false
      }
    }

    // Revoke key
    const revokeKey = async (keyId) => {
      if (!confirm('Are you sure you want to revoke this key?')) return

      revokingKey.value = keyId
      try {
        const response = await fetch(`/.netlify/functions/admin-community-keys?key_id=${keyId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken.value}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: 'Admin revoked via dashboard'
          })
        })

        if (response.ok) {
          await refreshData()
        }
      } catch (err) {
        console.error('Revoke error:', err)
        alert('Failed to revoke key')
      } finally {
        revokingKey.value = null
      }
    }

    // Copy key to clipboard
    const copyKey = async (key) => {
      try {
        await navigator.clipboard.writeText(key)
        alert('Key copied to clipboard')
      } catch (err) {
        alert('Failed to copy key')
      }
    }

    // Refresh data
    const refreshData = () => {
      loadDashboard()
    }

    // Format date
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }

    // Get display email (fallback to name field if email is empty)
    const getDisplayEmail = (key) => {
      // If email field exists and is not empty, use it
      if (key.email && key.email.trim()) {
        return key.email
      }
      
      // If email field is empty but name field contains an email-like string, use name
      if (key.name && key.name.includes('@')) {
        return key.name
      }
      
      // Fallback to email field even if empty, or show placeholder
      return key.email || key.name || 'No email'
    }

    // Debounced search
    let searchTimeout
    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        searchKeys()
      }, 500)
    }

    return {
      // Auth
      isAuthenticated,
      password,
      loading,
      error,
      login,
      logout,

      // Dashboard
      keys,
      stats,
      filters,
      pagination,
      searchKeys,
      loadMore,
      refreshData,
      formatDate,
      debouncedSearch,
      getDisplayEmail,

      // Create key
      newKey,
      creatingKey,
      createSuccess,
      createError,
      createKey,

      // Revoke
      revokingKey,
      revokeKey,
      copyKey
    }
  }
}
</script>

<style scoped>
.admin-dashboard {
  min-height: 100vh;
  background: #f5f5f5;
}

/* Login Styles */
.login-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.login-card {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.login-card h1 {
  margin-bottom: 10px;
  font-size: 28px;
  color: #333;
}

.login-card p {
  margin-bottom: 30px;
  color: #666;
}

/* Dashboard Styles */
.dashboard-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dashboard-header h1 {
  margin: 0;
  font-size: 32px;
  color: #333;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-card h3 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
}

.stat-value {
  margin: 0;
  font-size: 36px;
  font-weight: bold;
  color: #3b82f6;
}

/* Create Key Section */
.create-key-section {
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.create-key-section h2 {
  margin: 0 0 20px 0;
  font-size: 20px;
  color: #333;
}

.create-key-form .form-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.create-key-form .form-control {
  flex: 1;
  min-width: 200px;
}

/* Search Section */
.search-section {
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.search-section h2 {
  margin: 0 0 20px 0;
  font-size: 20px;
  color: #333;
}

.search-controls {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.search-controls .form-control {
  flex: 1;
  min-width: 150px;
}

/* Table Section */
.keys-table-section {
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.keys-table {
  width: 100%;
  border-collapse: collapse;
}

.keys-table th {
  text-align: left;
  padding: 12px;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
}

.keys-table td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.keys-table tbody tr:hover {
  background: #f9fafb;
}

/* Badges and Status */
.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.badge-paid {
  background: #10b981;
  color: white;
}

.badge-community {
  background: #3b82f6;
  color: white;
}

.badge-promotional {
  background: #8b5cf6;
  color: white;
}

.status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status.active {
  background: #d1fae5;
  color: #065f46;
}

.status.inactive {
  background: #fee2e2;
  color: #991b1b;
}

/* Form Controls */
.form-group {
  margin-bottom: 20px;
}

.form-control {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #4b5563;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-small {
  padding: 6px 12px;
  font-size: 14px;
}

/* Messages */
.error-message,
.success-message {
  margin-top: 10px;
  padding: 10px;
  border-radius: 6px;
  font-size: 14px;
}

.error-message {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.success-message {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

/* States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

/* Pagination */
.pagination {
  margin-top: 20px;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .search-controls,
  .create-key-form .form-row {
    flex-direction: column;
  }

  .keys-table {
    font-size: 14px;
  }

  .keys-table th,
  .keys-table td {
    padding: 8px;
  }
}
</style>